import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Button } from 'components/ui/button';
import { ArrowRight } from 'lucide-react';

interface HeroProps {
    onPlanSession: () => void;
}

export const Hero = ({ onPlanSession }: HeroProps) => {
    const { t } = useTranslation();
    return (
        <section className="relative bg-gradient-to-b from-cyan-50 via-white to-white py-20 lg:py-32 overflow-hidden">
            <div className="container mx-auto px-4 relative z-10">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="inline-block mb-6 px-4 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-semibold tracking-wide animate-fade-in-up shadow-sm">
                        {t('hero.badge')}
                    </div>
                    <h1 className="text-4xl md:text-6xl font-bold mb-6 text-gray-900 leading-tight tracking-tight opacity-0 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                        <Trans i18nKey="hero.title">
                            Votre Bien-être Mental, <br />
                            <span className="text-primary bg-clip-text text-transparent bg-gradient-to-r from-primary to-cyan-600">
                                Dans Votre Culture
                            </span>
                        </Trans>
                    </h1>
                    <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed opacity-0 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                        {t('hero.subtitle')}
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center opacity-0 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
                        <Button
                            size="lg"
                            className="bg-primary text-white hover:bg-primary/90 text-lg px-8 py-6 h-auto rounded-full shadow-xl hover:shadow-2xl transform transition-all duration-300 hover:-translate-y-1 hover:scale-105 w-full sm:w-auto ring-offset-2 focus-visible:ring-2"
                            onClick={onPlanSession}
                        >
                            {t('hero.cta')}
                            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                        </Button>
                        <Button
                            variant="outline"
                            size="lg"
                            className="text-lg px-8 py-6 h-auto rounded-full border-2 hover:bg-gray-50 w-full sm:w-auto transition-all duration-300 hover:border-primary/50 hover:scale-105"
                            onClick={() => {
                                document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
                            }}
                        >
                            {t('hero.howItWorks')}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Decorative background elements with floating animation */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10 pointer-events-none">
                <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary/10 rounded-full blur-3xl opacity-50 animate-float"></div>
                <div className="absolute top-1/2 -left-24 w-72 h-72 bg-blue-100/50 rounded-full blur-3xl opacity-50 animate-float" style={{ animationDelay: '2s' }}></div>
                <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-cyan-100/30 rounded-full blur-3xl animate-float" style={{ animationDelay: '4s' }}></div>
            </div>
        </section >
    );
};
